import { ButtonGroup } from '@components/ButtonGroup';
import { Checkbox } from '@components/Checkbox';
import { FormTextAreaField, FormTextField } from '@components/FormFields';
import { Label } from '@components/Label';
import { Modal } from '@components/Modal';
import * as yup from 'yup';
import { SegmentedControl } from '@components/SegmentedControl';
import { Box, FormControlLabel, Stack, Typography } from '@mui/material';
import { B2BNotificationData } from '@pages/Selections/RowSelection';
import { DeliveryChannel, notificationApiSlice, NotificationPriority } from '@src/api';
import {
  border,
  buildDataTestId,
  buildDataTestIdAttribute,
  buildFullDataTestId,
  log,
  replaceCebabCase,
} from '@src/utils';
import { FC, InputHTMLAttributes, useState } from 'react';
import { Controller, get, useForm } from 'react-hook-form';
import { schema } from './schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { channels, defaultValues, priorities } from './B2bNotificationModal.consts';
import { toast } from '@components/RootNotificationWrapper';

interface B2bNotificationModalProps {
  b2bNotificationData: B2BNotificationData;
  setOpen: (val: Nullable<B2BNotificationData>) => void;
}

export const B2bNotificationModal: FC<B2bNotificationModalProps> = ({ b2bNotificationData, setOpen }) => {
  const { description = '', selectionId } = b2bNotificationData;
  const [createNotification] = notificationApiSlice.useLazyCreateB2BNotificationsQuery();
  const { data: OPCounter } = notificationApiSlice.useGetCountOfOrderingProvidersQuery(selectionId);
  const [preview, setPreview] = useState(false);

  const {
    control,
    getValues,
    watch,
    trigger,
    formState: { isDirty },
  } = useForm<yup.InferType<typeof schema>>({
    resolver: yupResolver(schema),
    defaultValues: { ...defaultValues, description, selectionId },
    mode: 'onTouched',
  });

  const [openCancelModal, setOpenCancelModal] = useState(false);

  const onCancel = () => {
    if (isDirty) {
      setOpenCancelModal(true);
    } else {
      setOpen(null);
    }
  };

  const dataTestId = buildFullDataTestId('modal-b2b-notification');

  const onApply = () => {
    if (OPCounter && OPCounter.numberOfOrderingProvidersToNotify === 0) {
      toast({
        variant: 'error',
        message: 'In the current settings, no ordering provider is selected for notification.',
        title: 'Error',
      });
      return '';
    }
    if (preview) {
      const {
        notificationPriority,
        deliveryChannelTypes,
        selectionId: idSelection,
        subject,
        description: descriptionSelection,
      } = getValues();
      const data = {
        notificationPriority: notificationPriority as NotificationPriority,
        deliveryChannelTypes: deliveryChannelTypes as DeliveryChannel[],
        selectionId: idSelection,
        subject: subject,
        description: descriptionSelection || '',
      };
      createNotification({
        selectionId: data.selectionId,
        notificationPriority: data.notificationPriority,
        deliveryChannelTypes: data.deliveryChannelTypes,
        subject: data.subject,
        description: data.description,
      })
        .then((res: any) => {
          if (res.data.status === 201) {
            toast({
              variant: 'success',
              message: `Notification was successfully sent to ${OPCounter?.numberOfOrderingProvidersToNotify} recipients`,
              title: 'Success',
            });
            setOpen(null);
          }
          return '';
        })
        .catch(log);
    } else {
      trigger()
        .then(check => {
          if (check) {
            setPreview(true);
          }
          return null;
        })
        .catch(log);
    }
  };

  const selectedDeliveryChannels = watch('deliveryChannelTypes') || [];
  return (
    <>
      <Modal
        open={!!b2bNotificationData}
        title="Notification properties"
        bodySx={{ padding: 0 }}
        onCancel={onCancel}
        wrapperProps={{
          PaperProps: {
            sx: {
              maxWidth: '880px',
              maxHeight: '612px',
              width: '100%',
              height: '100%',
            },
            ...buildDataTestIdAttribute(dataTestId),
          },
        }}
      >
        <Box padding="24px" height="calc(100% - 96px)">
          {preview ? (
            <Stack>
              <Box padding="12px 0">
                <Typography
                  color="grey.700"
                  variant="body16Medium"
                  lineHeight="20px"
                >{`Are you sure you want to send the notification to ${
                  OPCounter?.numberOfOrderingProvidersToNotify || 0
                } recipients?`}</Typography>
              </Box>
              <Box sx={{ flexGrow: 1 }} />
            </Stack>
          ) : (
            <>
              <Stack gap="6px">
                <Typography variant="label14Bold" lineHeight="18px" color="grey.700">
                  Priority
                </Typography>
                <Controller
                  name="notificationPriority"
                  control={control}
                  render={({ field: { value, onChange } }) => {
                    const handleClick = (valueT: NotificationPriority) => {
                      onChange(valueT);
                    };
                    return (
                      <SegmentedControl
                        values={priorities}
                        active={value}
                        handleClick={handleClick}
                        fontWeight={600}
                        dataTestId={buildDataTestId(dataTestId, 'priority')}
                      />
                    );
                  }}
                />
              </Stack>
              <Box mt="24px">
                <FormTextField
                  name="subject"
                  label="Subject"
                  control={control}
                  required
                  maxLength={1000}
                  withMaxLenghtHelper
                  withErrorMessage
                  sx={{
                    '& .MuiTypography-body1': { marginBottom: '6px' },
                  }}
                  dataTestId={buildDataTestId(dataTestId, 'input-subject')}
                />
              </Box>
              <Box mt="30px" mb="30px">
                <FormTextAreaField
                  name="description"
                  placeholder=""
                  label="Description"
                  control={control}
                  maxLength={2500}
                  sx={{
                    '& .MuiTypography-body1': { marginBottom: '6px' },
                  }}
                  dataTestId={buildDataTestId(dataTestId, 'input-description')}
                />
              </Box>
              <Stack gap="12px">
                <Label label="Delivery channels" required />
                <Controller
                  name="deliveryChannelTypes"
                  control={control}
                  render={({ field: { value: fieldValue, onChange }, formState }) => {
                    const errorField = get(formState.errors, 'deliveryChannelTypes');
                    const error = errorField ? (errorField.message as string) : '';

                    return (
                      <Box>
                        <Stack direction="row" gap="24px">
                          {channels.map(({ name, value }) => {
                            const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
                              const { checked: checkedEvent } = event.target;
                              if (checkedEvent) {
                                onChange([...selectedDeliveryChannels, value]);
                              } else {
                                onChange(
                                  selectedDeliveryChannels.filter(selectedChannels => selectedChannels !== value),
                                );
                              }
                            };
                            return (
                              <FormControlLabel
                                key={value}
                                labelPlacement="end"
                                label={
                                  <Typography color="grey.700" variant="label14Medium" lineHeight="18px">
                                    {name}
                                  </Typography>
                                }
                                sx={{ gap: '3px' }}
                                control={
                                  <Checkbox
                                    error={!!error}
                                    checked={fieldValue?.includes(value)}
                                    onChange={handleChange}
                                    inputProps={
                                      {
                                        ...buildDataTestIdAttribute(dataTestId, `channels-${replaceCebabCase(value)}`),
                                      } as InputHTMLAttributes<HTMLInputElement>
                                    }
                                  />
                                }
                              />
                            );
                          })}
                        </Stack>
                        {error && (
                          <Typography
                            component="p"
                            color="error.200"
                            variant="label12Medium"
                            lineHeight="16px" /* sx={{ height: 0 }} */
                          >
                            Select at least one
                          </Typography>
                        )}
                      </Box>
                    );
                  }}
                />
              </Stack>
            </>
          )}
        </Box>
        <ButtonGroup
          applyName={`Send to ${OPCounter?.numberOfOrderingProvidersToNotify || 0} recipients`}
          onApply={onApply}
          onCancel={onCancel}
          cancelName="Cancel"
          sx={{ borderTop: border, padding: '24px', marginLeft: 0 }}
          dataTestId={dataTestId}
        />
      </Modal>
      <Modal open={openCancelModal} title="Cancel notification">
        <Box padding="12px 0 36px">
          <Typography>All changes will be lost. Proceed?</Typography>
        </Box>
        <ButtonGroup
          onApply={() => {
            setOpen(null);
          }}
          onCancel={() => {
            setOpenCancelModal(false);
          }}
          applyName="Yes"
          cancelName="No"
          dataTestId={buildDataTestId(dataTestId, 'modal-cancel')}
        />
      </Modal>
    </>
  );
};
